import { useState, useEffect } from "react";
import { tagsSchemas, type tagsTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { EntitySelector } from "../components/EntitySelector";
import { Modal } from "../components/Modal";
import { SSESubscriptionControl } from "../components/SSESubscriptionControl";
import { useAdmin } from "../context/AdminContext";
import type { Uuid } from "@pineseed/spec";

type Tag = tagsTypes["Tag"];
type TagAssignment = tagsTypes["TagAssignment"];
type View = 'tags' | 'assignments' | 'stats' | 'relationships';

const LAST_TAGGER_VIEW_KEY = 'admin-last-tagger-view';

export function TaggerPanel() {
  const { adminApp, tagger } = useAdmin();
  
  const [view, setView] = useState<View>(() => {
    const saved = localStorage.getItem(LAST_TAGGER_VIEW_KEY) as View | null;
    return saved || 'tags';
  });

  // Persist view
  useEffect(() => {
    localStorage.setItem(LAST_TAGGER_VIEW_KEY, view);
  }, [view]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  
  // Filters
  const [namespaceFilter, setNamespaceFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<Tag['type'] | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Relationship view state
  const [selectedTagForEntities, setSelectedTagForEntities] = useState<Tag | null>(null);
  const [entitiesWithTag, setEntitiesWithTag] = useState<Array<{ entityType: TagAssignment['entityType']; entityId: string }>>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  // Load data based on view and filters
  useEffect(() => {
    if (view === 'tags' || view === 'stats' || view === 'relationships') {
      // Apply filters
      if (namespaceFilter) {
        tagger.filterByNamespace(namespaceFilter);
      } else if (typeFilter) {
        tagger.filterByType(typeFilter);
      } else {
        tagger.loadEntities();
      }
    } else if (view === 'assignments') {
      tagger.loadAssignments();
    }
  }, [view, namespaceFilter, typeFilter]);

  // Search handler
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      await tagger.clearFilters();
      return;
    }
    await tagger.searchTags(searchQuery, {
      namespace: namespaceFilter || undefined,
      type: typeFilter || undefined,
    });
  };

  // CRUD handlers
  const handleCreate = async (data: Partial<Tag>) => {
    try {
      await tagger.createEntity({
        name: data.name!,
        type: data.type!,
        workspaceId: adminApp.workspaceId as Uuid,
        namespace: adminApp.namespace,
        ...(data.color && { color: data.color }),
        ...(data.icon && { icon: data.icon }),
        ...(data.description && { description: data.description }),
        ...(data.metadata && { metadata: data.metadata }),
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create tag:', error);
      throw error;
    }
  };

  const handleEdit = async (data: Partial<Tag>) => {
    if (!editingTag) return;
    try {
      await tagger.updateEntity(editingTag.id, data);
      setEditingTag(null);
    } catch (error) {
      console.error('Failed to update tag:', error);
      throw error;
    }
  };

  const handleDelete = async (tag: Tag) => {
    if (!confirm(`Delete tag "${tag.name}"?\n\nThis will also remove all assignments.`)) return;
    try {
      await tagger.deleteEntity(tag.id);
    } catch (error) {
      console.error('Failed to delete tag:', error);
    }
  };

  // Assignment handlers
  const handleAssign = async (data: Partial<TagAssignment>) => {
    try {
      await tagger.assignTag({
        tagId: data.tagId!,
        entityType: data.entityType!,
        entityId: data.entityId!,
      });
      setShowAssignForm(false);
    } catch (error) {
      console.error('Failed to assign tag:', error);
      throw error;
    }
  };

  const handleUnassign = async (assignment: TagAssignment) => {
    if (!confirm('Remove this tag assignment?')) return;
    try {
      await tagger.unassignTag(assignment);
    } catch (error) {
      console.error('Failed to unassign tag:', error);
    }
  };

  // Handle loading entities for a specific tag
  const handleLoadEntitiesForTag = async (tag: Tag) => {
    setSelectedTagForEntities(tag);
    setLoadingEntities(true);
    try {
      const entities = await tagger.getEntitiesWithTag(tag.id);
      setEntitiesWithTag(entities);
    } catch (error) {
      console.error('Failed to load entities for tag:', error);
      setEntitiesWithTag([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  // Get statistics from handler
  const stats = tagger.getStats();

  // View: Tags list
  const tagsColumns: Column<Tag>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (tag) => (
        <div className="flex items-center gap-2">
          {tag.color && (
            <div
              className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
              style={{ backgroundColor: tag.color }}
            />
          )}
          <span className="font-medium">{tag.name}</span>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (tag) => {
        const typeColors: Record<string, string> = {
          participant: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
          group: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
          general: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
        };
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${typeColors[tag.type]}`}>
            {tag.type}
          </span>
        );
      },
    },
    {
      key: 'namespace',
      label: 'Namespace',
      sortable: true,
      render: (tag) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{tag.namespace}</span>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (tag) =>
        tag.description ? (
          <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-xs block">
            {tag.description}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'icon',
      label: 'Icon',
      render: (tag) =>
        tag.icon ? (
          <span className="text-sm">{tag.icon}</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (tag) => (
        <span className="text-xs text-gray-500">{new Date(tag.createdAt).toLocaleDateString()}</span>
      ),
    },
  ];

  // View: Assignments list
  const assignmentsColumns: Column<TagAssignment>[] = [
    {
      key: 'tagId',
      label: 'Tag',
      render: (assignment) => {
        const tag = tagger.entities.find((t) => t.id === assignment.tagId);
        return tag ? (
          <div className="flex items-center gap-2">
            {tag.color && (
              <div
                className="w-3 h-3 rounded border border-gray-300"
                style={{ backgroundColor: tag.color }}
              />
            )}
            <span className="text-sm font-medium">{tag.name}</span>
          </div>
        ) : (
          <span className="text-xs text-gray-500 font-mono">{assignment.tagId.slice(0, 8)}...</span>
        );
      },
    },
    {
      key: 'entityType',
      label: 'Entity Type',
      sortable: true,
      render: (assignment) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">{assignment.entityType}</span>
      ),
    },
    {
      key: 'entityId',
      label: 'Entity ID',
      render: (assignment) => (
        <span className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate max-w-xs block">
          {assignment.entityId}
        </span>
      ),
    },
    {
      key: 'assignedBy',
      label: 'Assigned By',
      render: (assignment) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{assignment.assignedBy}</span>
      ),
    },
    {
      key: 'assignedAt',
      label: 'Assigned',
      sortable: true,
      render: (assignment) => (
        <span className="text-xs text-gray-500">
          {new Date(assignment.assignedAt).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Tag Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage tags and their assignments across the workspace
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAssignForm(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Assign Tag
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Tag
          </button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {(['tags', 'assignments', 'relationships', 'stats'] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 border-b-2 transition-colors ${
                view === v
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
              {v === 'tags' && <span className="ml-2 text-xs">({stats.total})</span>}
              {v === 'assignments' && (
                <span className="ml-2 text-xs">({stats.assignmentsCount})</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      {view === 'tags' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search tags..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as Tag['type'] | '')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Types</option>
                <option value="participant">Participant</option>
                <option value="group">Group</option>
                <option value="general">General</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Namespace
              </label>
              <input
                type="text"
                value={namespaceFilter}
                onChange={(e) => setNamespaceFilter(e.target.value)}
                placeholder="e.g., general"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <SSESubscriptionControl
              enabled={tagger.sseEnabled}
              onToggle={tagger.setSseEnabled}
              includeInitial={tagger.includeInitialData}
              onIncludeInitialToggle={tagger.setIncludeInitialData}
              label="Live Updates"
            />
            <div className="flex items-end gap-2">
              <button
                onClick={searchQuery ? handleSearch : () => tagger.refresh()}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                {searchQuery ? 'Search' : 'Refresh'}
              </button>
              {(searchQuery || typeFilter || namespaceFilter) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('');
                    setNamespaceFilter('');
                    tagger.clearFilters();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats View */}
      {view === 'stats' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Overview
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Tags:</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{stats.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Assignments:</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">
                  {stats.assignmentsCount}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              By Type
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.byType).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {type}:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              By Namespace
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Object.entries(stats.byNamespace).map(([namespace, count]) => (
                <div key={namespace} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{namespace}:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tags Table */}
      {view === 'tags' && (
        <EntityTable
          data={tagger.entities}
          columns={tagsColumns}
          onEdit={setEditingTag}
          onDelete={handleDelete}
          loading={tagger.loading}
          emptyMessage="No tags found. Create your first tag to get started."
        />
      )}

      {/* Assignments Table */}
      {view === 'assignments' && (
        <EntityTable
          data={tagger.assignments}
          columns={assignmentsColumns}
          onDelete={handleUnassign}
          loading={tagger.assignmentsLoading}
          emptyMessage="No tag assignments found."
        />
      )}

      {/* Relationships View */}
      {view === 'relationships' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: Tag List */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Select a Tag
            </h3>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {tagger.loading ? (
                <p className="text-sm text-gray-500">Loading tags...</p>
              ) : tagger.entities.length === 0 ? (
                <p className="text-sm text-gray-500">No tags found.</p>
              ) : (
                tagger.entities.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleLoadEntitiesForTag(tag)}
                    className={`w-full text-left p-3 rounded border transition-colors ${
                      selectedTagForEntities?.id === tag.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {tag.color && (
                        <div
                          className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {tag.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {tag.type} • {tag.namespace}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: Entities with selected tag */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {selectedTagForEntities
                ? `Entities with "${selectedTagForEntities.name}"`
                : 'Entities'}
            </h3>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {!selectedTagForEntities ? (
                <p className="text-sm text-gray-500">
                  Select a tag to see which entities have it assigned.
                </p>
              ) : loadingEntities ? (
                <p className="text-sm text-gray-500">Loading entities...</p>
              ) : entitiesWithTag.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No entities have this tag assigned.
                </p>
              ) : (
                entitiesWithTag.map((entity, idx) => (
                  <div
                    key={`${entity.entityType}-${entity.entityId}-${idx}`}
                    className="p-3 rounded border border-gray-300 dark:border-gray-600"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {entity.entityType.charAt(0).toUpperCase() + entity.entityType.slice(1)}
                        </div>
                        <div className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate">
                          {entity.entityId}
                        </div>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        {entity.entityType}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Tag Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Tag"
      >
        <EntityForm
          schema={tagsSchemas.CreateTagRequest}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
          submitLabel="Create"
        />
      </Modal>

      {/* Edit Tag Modal */}
      <Modal
        isOpen={!!editingTag}
        onClose={() => setEditingTag(null)}
        title="Edit Tag"
      >
        {editingTag && (
          <EntityForm
            schema={tagsSchemas.UpdateTagRequest}
            initialData={editingTag}
            onSubmit={handleEdit}
            onCancel={() => setEditingTag(null)}
            submitLabel="Update"
          />
        )}
      </Modal>

      {/* Assign Tag Modal */}
      <Modal
        isOpen={showAssignForm}
        onClose={() => setShowAssignForm(false)}
        title="Assign Tag to Entity"
      >
        <div className="p-6">
          <AssignTagForm
            tags={tagger.entities}
            onSubmit={handleAssign}
            onCancel={() => setShowAssignForm(false)}
          />
        </div>
      </Modal>
    </div>
  );
}

// Separate form for tag assignment
function AssignTagForm({
  tags,
  onSubmit,
  onCancel,
}: {
  tags: Tag[];
  onSubmit: (data: Partial<TagAssignment>) => Promise<void>;
  onCancel: () => void;
}) {
  const [tagId, setTagId] = useState('');
  const [entityType, setEntityType] = useState<TagAssignment['entityType']>('participant');
  const [entityId, setEntityId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tagId || !entityId) return;

    setSubmitting(true);
    try {
      await onSubmit({ tagId, entityType, entityId });
    } catch (error) {
      console.error('Assignment failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Tag <span className="text-red-500">*</span>
        </label>
        <select
          value={tagId}
          onChange={(e) => setTagId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Select a tag...</option>
          {tags.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name} ({tag.type}) [{tag.namespace}]
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Entity Type <span className="text-red-500">*</span>
        </label>
        <select
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value as TagAssignment['entityType']);
            setEntityId(''); // Reset entity selection when type changes
          }}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="participant">Participant</option>
          <option value="group">Group</option>
          <option value="message">Message</option>
          <option value="thread">Thread</option>
          <option value="workspace">Workspace</option>
          <option value="file">File</option>
        </select>
      </div>

      <EntitySelector
        entityType={entityType as 'participant' | 'group' | 'file' | 'chat-message' | 'workspace' | 'thread' | 'tag'}
        value={entityId}
        onChange={setEntityId}
        required
        label={`${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`}
        placeholder={`Select ${entityType}...`}
      />

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !tagId || !entityId}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          {submitting ? 'Assigning...' : 'Assign Tag'}
        </button>
      </div>
    </form>
  );
}
